/**
 * Shared JWT helpers for accessToken / token in localStorage.
 */

export function getAccessToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('accessToken') || localStorage.getItem('token') || null;
}

/** True if missing, malformed, or exp is not after now (15s skew). */
export function isAccessTokenMissingOrExpired(token) {
  try {
    if (!token || typeof token !== 'string') return true;
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expSeconds = payload?.exp;
    if (!expSeconds) return true;
    const nowMs = Date.now();
    return expSeconds * 1000 <= nowMs + 15_000;
  } catch (_) {
    return true;
  }
}

/** True when we should call authenticated APIs (billing, team refresh, etc.). */
export function hasUsableAccessToken() {
  return !isAccessTokenMissingOrExpired(getAccessToken());
}

/**
 * True when there is a session at all — distinct from hasUsableAccessToken(),
 * which is also false for a token that merely expires within 15s.
 *
 * That distinction matters for any one-shot, non-retrying bootstrap effect
 * deciding whether to attempt its first authenticated call. axiosInstance's
 * response interceptor already refreshes and retries any real request that
 * gets a 401 — that IS the refresh mechanism, there is no separate proactive
 * one. A gate that refuses to fire just because the token has, say, 10 seconds
 * left never gives that request a chance to happen, so the refresh it would
 * have triggered never happens either — and with no retry scheduled, the
 * caller is stuck showing stale/incomplete state until something unrelated
 * happens to re-run it. Use hasUsableAccessToken() for calls that would be
 * wasted work if the token is already known to be expiring (e.g. right before
 * an expensive prefetch); use this for "is anyone logged in at all".
 */
export function hasAnyAccessToken() {
  return !!getAccessToken();
}
